import { useState, useEffect } from 'react';
import { Shield, Key, Clock, AlertTriangle, CheckCircle, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { SecurityService } from '../../lib/securityService';
import { runMigrationIfNeeded, checkEncryptionStatus } from '../../scripts/migrateApiKeysEncryption';

// ============================================================================
// TYPES ET INTERFACES
// ============================================================================

interface EncryptionStatus {
  total: number;
  encrypted: number;
  plaintext: number;
  details: Array<{ service: string; encrypted: boolean }>;
}

interface RateLimitStats {
  ip: string;
  endpoint: string;
  requests: number;
  windowStart: number;
  lastRequest: number;
  config: {
    windowMs: number;
    maxRequests: number;
  };
}

interface SecurityTestResult {
  encryption: {
    success: boolean;
    message: string;
  };
  tokenGeneration: {
    success: boolean;
    message: string;
    token?: string;
  };
  rateLimit: {
    success: boolean;
    message: string;
  };
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export function SecurityConfigSection() {
  const [encryptionStatus, setEncryptionStatus] = useState<EncryptionStatus | null>(null);
  const [rateLimitStats, setRateLimitStats] = useState<RateLimitStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [testResults, setTestResults] = useState<SecurityTestResult | null>(null);
  const [showTestToken, setShowTestToken] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // ============================================================================
  // FONCTIONS UTILITAIRES
  // ============================================================================

  /**
   * Charge l'état du chiffrement des clés API
   */
  const loadEncryptionStatus = async () => {
    try {
      setIsLoading(true);
      const status = await checkEncryptionStatus();
      setEncryptionStatus(status);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'état du chiffrement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Lance la migration des clés API
   */
  const handleMigration = async () => {
    try {
      setIsMigrating(true);
      await runMigrationIfNeeded();
      await loadEncryptionStatus();
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Erreur lors de la migration:', error);
    } finally {
      setIsMigrating(false);
    }
  };

  /**
   * Teste les fonctionnalités de sécurité
   */
  const runSecurityTests = async () => {
    setIsLoading(true);
    const results: SecurityTestResult = {
      encryption: { success: false, message: '' },
      tokenGeneration: { success: false, message: '' },
      rateLimit: { success: false, message: '' }
    };

    try {
      // Test du chiffrement/déchiffrement
      try {
        const testKey = 'test-api-key-12345';
        const encrypted = await SecurityService.encryptApiKey(testKey);
        const decrypted = await SecurityService.decryptApiKey(encrypted);
        
        if (decrypted === testKey) {
          results.encryption = {
            success: true,
            message: 'Chiffrement/déchiffrement fonctionnel'
          };
        } else {
          results.encryption = {
            success: false,
            message: 'Erreur: clé déchiffrée différente de l\'originale'
          };
        }
      } catch (error) {
        results.encryption = {
          success: false,
          message: `Erreur de chiffrement: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
        };
      }

      // Test de génération de token
      try {
        const token = SecurityService.generateSecureToken(8);
        const isValid = SecurityService.validateQuizToken(token);
        
        if (isValid && token.length === 8) {
          results.tokenGeneration = {
            success: true,
            message: 'Génération de token fonctionnelle',
            token
          };
        } else {
          results.tokenGeneration = {
            success: false,
            message: 'Token généré invalide'
          };
        }
      } catch (error) {
        results.tokenGeneration = {
          success: false,
          message: `Erreur de génération: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
        };
      }

      // Test du rate limiting
      try {
        const testIP = '127.0.0.1';
        const testEndpoint = '/api/test';
        
        const result1 = SecurityService.validateRateLimit(testIP, testEndpoint);
        const result2 = SecurityService.validateRateLimit(testIP, testEndpoint);
        
        if (result1.allowed && result2.remaining < result1.remaining) {
          results.rateLimit = {
            success: true,
            message: 'Rate limiting fonctionnel'
          };
        } else {
          results.rateLimit = {
            success: false,
            message: 'Rate limiting ne fonctionne pas correctement'
          };
        }
        
        // Nettoie le test
        SecurityService.resetRateLimit(testIP, testEndpoint);
      } catch (error) {
        results.rateLimit = {
          success: false,
          message: `Erreur de rate limiting: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
        };
      }

    } catch (error) {
      console.error('Erreur lors des tests de sécurité:', error);
    }

    setTestResults(results);
    setIsLoading(false);
  };

  /**
   * Nettoie tous les rate limits
   */
  const clearAllRateLimits = () => {
    SecurityService.clearAllRateLimits();
    setRateLimitStats([]);
    setLastUpdate(new Date());
  };

  // ============================================================================
  // EFFETS
  // ============================================================================

  useEffect(() => {
    loadEncryptionStatus();
  }, []);

  // ============================================================================
  // RENDU
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Configuration de Sécurité</h2>
        </div>
        <div className="text-sm text-gray-500">
          Dernière mise à jour: {lastUpdate.toLocaleTimeString()}
        </div>
      </div>

      {/* État du chiffrement des clés API */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Key className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-medium text-gray-900">Chiffrement des Clés API</h3>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={loadEncryptionStatus}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            {encryptionStatus && encryptionStatus.plaintext > 0 && (
              <button
                onClick={handleMigration}
                disabled={isMigrating}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {isMigrating ? (
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Shield className="h-4 w-4 mr-1" />
                )}
                Migrer
              </button>
            )}
          </div>
        </div>

        {isLoading && !encryptionStatus ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Chargement...</span>
          </div>
        ) : encryptionStatus ? (
          <div className="space-y-4">
            {/* Statistiques */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{encryptionStatus.total}</div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{encryptionStatus.encrypted}</div>
                <div className="text-sm text-gray-600">Chiffrées</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{encryptionStatus.plaintext}</div>
                <div className="text-sm text-gray-600">Plain text</div>
              </div>
            </div>

            {/* Détails par service */}
            {encryptionStatus.details.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-900">Détails par service:</h4>
                <div className="space-y-1">
                  {encryptionStatus.details.map((detail, index) => (
                    <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                      <span className="text-sm font-medium text-gray-900">{detail.service}</span>
                      <div className="flex items-center space-x-1">
                        {detail.encrypted ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-600">Chiffrée</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                            <span className="text-sm text-red-600">Plain text</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Alerte si des clés ne sont pas chiffrées */}
            {encryptionStatus.plaintext > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Clés API non chiffrées détectées
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        {encryptionStatus.plaintext} clé(s) API sont stockées en plain text. 
                        Il est recommandé de les chiffrer pour améliorer la sécurité.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Aucune donnée disponible
          </div>
        )}
      </div>

      {/* Tests de sécurité */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Tests de Sécurité</h3>
          </div>
          <button
            onClick={runSecurityTests}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            Lancer les tests
          </button>
        </div>

        {testResults && (
          <div className="space-y-3">
            {/* Test de chiffrement */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              testResults.encryption.success ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex items-center space-x-2">
                <Key className={`h-4 w-4 ${testResults.encryption.success ? 'text-green-600' : 'text-red-600'}`} />
                <span className="text-sm font-medium">Chiffrement/Déchiffrement</span>
              </div>
              <div className="flex items-center space-x-2">
                {testResults.encryption.success ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                )}
                <span className={`text-sm ${testResults.encryption.success ? 'text-green-600' : 'text-red-600'}`}>
                  {testResults.encryption.message}
                </span>
              </div>
            </div>

            {/* Test de génération de token */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              testResults.tokenGeneration.success ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex items-center space-x-2">
                <RefreshCw className={`h-4 w-4 ${testResults.tokenGeneration.success ? 'text-green-600' : 'text-red-600'}`} />
                <span className="text-sm font-medium">Génération de Token</span>
              </div>
              <div className="flex items-center space-x-2">
                {testResults.tokenGeneration.success ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                )}
                <span className={`text-sm ${testResults.tokenGeneration.success ? 'text-green-600' : 'text-red-600'}`}>
                  {testResults.tokenGeneration.message}
                </span>
                {testResults.tokenGeneration.token && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setShowTestToken(!showTestToken)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      {showTestToken ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </button>
                    {showTestToken && (
                      <code className="text-xs bg-gray-100 px-1 rounded">
                        {testResults.tokenGeneration.token}
                      </code>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Test de rate limiting */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              testResults.rateLimit.success ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex items-center space-x-2">
                <Clock className={`h-4 w-4 ${testResults.rateLimit.success ? 'text-green-600' : 'text-red-600'}`} />
                <span className="text-sm font-medium">Rate Limiting</span>
              </div>
              <div className="flex items-center space-x-2">
                {testResults.rateLimit.success ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                )}
                <span className={`text-sm ${testResults.rateLimit.success ? 'text-green-600' : 'text-red-600'}`}>
                  {testResults.rateLimit.message}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gestion du Rate Limiting */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-medium text-gray-900">Rate Limiting</h3>
          </div>
          <button
            onClick={clearAllRateLimits}
            className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            Nettoyer tout
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Configuration par défaut:</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <div>• Quiz: 10 requêtes/minute</div>
              <div>• Prospects: 50 requêtes/minute</div>
              <div>• PageSpeed: 5 requêtes/5 minutes</div>
              <div>• SEO: 10 requêtes/5 minutes</div>
              <div>• OpenAI: 20 requêtes/minute</div>
              <div>• Auth: 5 requêtes/15 minutes</div>
              <div>• Défaut: 100 requêtes/minute</div>
            </div>
          </div>

          {rateLimitStats.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">Statistiques actuelles:</h4>
              {rateLimitStats.map((stat, index) => (
                <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{stat.ip}</span>
                    <span className="text-sm text-gray-500 ml-2">{stat.endpoint}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {stat.requests}/{stat.config.maxRequests} requêtes
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">
              Aucune activité de rate limiting récente
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SecurityConfigSection;
